import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Container,
  useTheme,
  useMediaQuery,
  LinearProgress,
  Chip,
  Card,
  CardContent,
  Fade,
  Zoom,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material';
import DarkModeToggle from '../shared/DarkModeToggle';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import { LeaseFormData } from '../../types/lease.types';

import { 
  CheckCircle as CheckIcon,
  Download as DownloadIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Celebration as CelebrationIcon,
  Description as DescriptionIcon,
  Print as PrintIcon,
  Edit as EditIcon,
  Check as CheckIcon2,
  Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { saveDraftLease, getLeaseAgreement, generateLeaseDocument, updateLeaseAgreement, updateLeaseStatus } from '../../services/leaseService';
import { useStripe } from '../../contexts/FirebaseStripeContext';
import NavBar from '../NavBar/NavBar';
import SaveLeaseDialog from '../SaveLeaseDialog';
import PaywallDialog from '../PaywallDialog';
import PDFGenerationLoader from '../PDFGenerationLoader';
import { useWaitlist } from '../../hooks/useWaitlist';
import { generateDemoLeaseData } from '../../utils/demoLeaseData';

// Import step components
import LandlordInfoStep from './steps/LandlordInfoStep';
import PropertyInfoStep from './steps/PropertyInfoStep';
import TenantInfoStep from './steps/TenantInfoStep';
import LeaseTermsStep from './steps/LeaseTermsStep';
import FinancialTermsStep from './steps/FinancialTermsStep';
import PropertyPoliciesStep from './steps/PropertyPoliciesStep';
import UtilitiesStep from './steps/UtilitiesStep';
import PropertyFeaturesStep from './steps/PropertyFeaturesStep';
import DisclosuresStep from './steps/DisclosuresStep';
import AdditionalTermsStep from './steps/AdditionalTermsStep';
import ReviewStep from './steps/ReviewStep';

const steps = [
  'Landlord Information',
  'Property Details', 
  'Tenant Information',
  'Lease Terms',
  'Financial Terms',
  'Property Policies',
  'Utilities & Services',
  'Property Features',
  'Disclosures',
  'Additional Terms',
  'Review & Generate',
];

const shortSteps = [
  'Landlord',
  'Property',
  'Tenants', 
  'Terms',
  'Finance',
  'Policies',
  'Utilities',
  'Features',
  'Disclosures',
  'Additional',
  'Review',
];

const LeaseForm: React.FC = () => {
  const { leaseId } = useParams<{ leaseId?: string }>();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveDialogAction, setSaveDialogAction] = useState<'draft' | 'generate'>('draft');
  const [currentLeaseId, setCurrentLeaseId] = useState<string | undefined>(leaseId);
  const [currentLeaseName, setCurrentLeaseName] = useState<string>('');
  const [showPaywall, setShowPaywall] = useState(false);
  const [isEditingLeaseName, setIsEditingLeaseName] = useState(false);
  const [tempLeaseName, setTempLeaseName] = useState<string>('');
  
  // Environment variable to control mobile progress visibility
  const hideMobileProgress = process.env.REACT_APP_HIDE_MOBILE_PROGRESS === 'true';

  // Update tempLeaseName when currentLeaseName changes
  useEffect(() => {
    setTempLeaseName(currentLeaseName);
  }, [currentLeaseName]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' | 'info' });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useSelector((state: RootState) => state.auth);
  const { hasAccess } = useStripe();
  const formContentRef = useRef<HTMLDivElement>(null);

  // Check if user has access to printing and subscriptions
  const { hasPrintingAccess, config } = useWaitlist();
  const isDevelopment = process.env.NODE_ENV !== 'production';

  const methods = useForm<LeaseFormData>({
    mode: 'onChange',
    defaultValues: {
      landlord: {
        fullName: '',
        email: '',
        phone: '',
        address: {
          street: '',
          city: '',
          state: '',
          zip: '',
        },
      },
      property: {
        address: {
          street: '',
          city: '',
          state: '',
          zip: '',
        },
        type: 'apartment',
        yearBuilt: new Date().getFullYear(),
        bedrooms: 1,
        bathrooms: 1,
        hasHOA: false,
      },
      tenants: [{
        fullName: '',
        email: '',
        phone: '',
        isPrimary: true,
        emergencyContact: {
          name: '',
          phone: '',
          relationship: '',
        },
      }],
      additionalOccupants: [],
      terms: {
        type: 'month-to-month',
        startDate: '',
        noticeRequired: 30,
        rentIncreaseNotice: 30,
      },
      rent: {
        monthlyAmount: 0,
        dueDay: 1,
        dueTime: '',
        prorated: 0,
        lateFee: {
          type: 'fixed',
          amount: 0,
          gracePeriod: 3,
        },
        paymentMethods: [],
      },
      deposits: {
        security: 0,
        key: 0,
        otherDeposits: [],
        paymentPlan: false,
        maxAllowed: 0,
      },
      fees: {
        nsfFee: 25,
        nonRefundableTotal: 0,
        nonRefundableDescription: '',
      },
      policies: {
        pets: {
          allowed: false,
          types: [],
          count: 0,
          weightLimit: 0,
          deposit: 0,
          rent: 0,
          details: [],
          notes: '',
        },
        smoking: {
          policy: 'not_permitted',
        },
        guests: {
          allowed: true,
          overnightAllowed: true,
          maxNights: 14,
        },
        quietHours: {
          enabled: false,
          startTime: '',
          endTime: '',
        },
        businessUse: false,
        modifications: {
          painting: false,
          mounting: false,
        },
        insurance: {
          required: false,
          minimumCoverage: 100000,
        },
      },
      utilities: {
        electric: { responsibility: 'na' },
        gas: { responsibility: 'na' },
        water: { responsibility: 'na' },
        sewer: { responsibility: 'na' },
        trash: { responsibility: 'na' },
        internet: { responsibility: 'na' },
        cable: { responsibility: 'na' },
        lawnCare: { responsibility: 'na' },
        snowRemoval: { responsibility: 'na' },
        sharedUtilities: { hasShared: false },
        billingNoticeDays: 30,
      },
      features: {
        parking: {
          included: false,
          types: [],
          spaces: 0,
          permitRequired: false,
        },
        storage: {
          included: false,
        },
        laundry: {
          type: 'none',
        },
        appliances: {
          included: [],
        },
        hvac: {
          heating: '',
          cooling: '',
        },
        security: {
          system: false,
        },
        access: {
          keyCount: 2,
          keyTypes: [],
          garageRemotes: 0,
          gateFobs: 0,
          otherAccessDevices: '',
          accessCodes: '',
          notes: '',
          keyReplacementFee: 25,
        },
        furnished: false,
      },
      disclosures: {
        leadPaint: {
          applicable: false,
        },
        asbestos: {
          status: 'unknown',
        },
        mold: {
          status: 'none_visible',
        },
        bedBugs: {
          history: false,
        },
        methContamination: {
          status: 'none',
        },
        deathOnProperty: {
          occurred: false,
        },
        militarySite: {
          within1Mile: false,
        },
        floodZone: {
          inFloodZone: false,
        },
        earthquakeFault: {
          inZone: false,
        },
        noiseIssues: {
          hasIssues: false,
        },
        pestControl: {
          regularTreatment: false,
        },
        tpa: {
          exempt: false,
          overallStatus: 'unknown',
          rentCapStatus: 'unknown',
          justCauseStatus: 'unknown',
        },
      },
      additionalTerms: {
        rules: '',
        maintenanceResponsibilities: '',
        emergencyProcedures: '',
        moveOutRequirements: '',
        specialProvisions: '',
        communicationPreference: 'email',
        maintenanceRequestMethod: 'Email to landlord',
        maintenanceCommunicationMethods: [],
      },
      tenantNoticeAddress: {
        street: '',
        city: '',
        state: '',
        zip: '',
      },
      status: 'draft',
      version: 1,
    },
  });

  // Load existing lease data if editing
  useEffect(() => {
    const loadLeaseData = async () => {
      if (leaseId && user) {
        try {
          const leaseData = await getLeaseAgreement(leaseId);
          if (leaseData && leaseData.createdBy === user.uid) {
            methods.reset(leaseData);
            setCurrentLeaseName(leaseData.name || '');
            
            // Show warning if editing an active or terminated lease
            if (leaseData.status === 'active' || leaseData.status === 'terminated') {
              const statusText = leaseData.status === 'active' ? 'active' : 'terminated';
              setSnackbar({
                open: true,
                message: `⚠️ You are editing a ${statusText} lease: "${leaseData.name || 'Unnamed Lease'}". Changes will overwrite the existing data.`,
                severity: 'warning'
              });
            }
          } else {
            setSnackbar({
              open: true,
              message: 'Lease not found or you do not have permission to edit it',
              severity: 'error'
            });
            navigate('/dashboard');
          }
        } catch (error) {
          console.error('Error loading lease:', error);
          setSnackbar({
            open: true,
            message: 'Failed to load lease data',
            severity: 'error'
          });
        }
      }
    };

    loadLeaseData();
  }, [leaseId, user, methods, navigate]);

  // Scroll to top when step changes
  useEffect(() => {
    if (formContentRef.current) {
      const elementTop = formContentRef.current.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementTop - 90,
        behavior: 'smooth'
      });
    }
  }, [activeStep]);

  // Watch form data for overall progress calculation
  const formData = useWatch({ control: methods.control });
  
  // Calculate overall form completion - only count required steps (0-4)
  const calculateOverallProgress = () => {
    const requiredStepProgresses = [
      calculateStepProgress(0, formData), // Landlord Info
      calculateStepProgress(1, formData), // Property Details
      calculateStepProgress(2, formData), // Tenant Info
      calculateStepProgress(3, formData), // Lease Terms
      calculateStepProgress(4, formData), // Financial Terms
    ];
    
    return requiredStepProgresses.reduce((acc, curr) => acc + curr, 0) / requiredStepProgresses.length;
  };

  // Helper function to check if a value is different from its default
  const isUserFilled = (value: any, defaultValue: any) => {
    if (value === undefined || value === null) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    return value !== defaultValue;
  };

  // Calculate progress for individual steps
  const calculateStepProgress = (stepIndex: number, data: any): number => {
    switch (stepIndex) {
      case 0: // Landlord Info - no defaults, check all required fields
        const landlordFields = [data?.landlord?.fullName, data?.landlord?.email, data?.landlord?.phone, 
                              data?.landlord?.address?.street, data?.landlord?.address?.city, 
                              data?.landlord?.address?.state, data?.landlord?.address?.zip];
        return (landlordFields.filter(f => f && f.toString().trim() !== '').length / landlordFields.length) * 100;
      
      case 1: // Property Info - exclude defaults
        const propertyChecks = [
          // Only count these if user actually filled them (no defaults)
          isUserFilled(data?.property?.address?.street, ''),
          isUserFilled(data?.property?.address?.city, ''),
          isUserFilled(data?.property?.address?.state, ''),
          isUserFilled(data?.property?.address?.zip, ''),
          // Exclude defaulted fields from progress calculation
          isUserFilled(data?.property?.type, 'apartment'),
          isUserFilled(data?.property?.yearBuilt, new Date().getFullYear()),
          isUserFilled(data?.property?.bedrooms, 1),
          isUserFilled(data?.property?.bathrooms, 1),
        ];
        const userFilledPropertyFields = propertyChecks.filter(Boolean).length;
        const requiredPropertyFields = 4; // Only count the 4 address fields as required for progress
        return Math.min((userFilledPropertyFields / requiredPropertyFields) * 100, 100);
      
      case 2: // Tenant Info - no defaults to exclude
        if (!data?.tenants || data.tenants.length === 0) return 0;
        let totalTenantFields = 0;
        let completedTenantFields = 0;
        data.tenants.forEach((tenant: any) => {
          const fields = [tenant?.fullName, tenant?.email, tenant?.phone,
                         tenant?.emergencyContact?.name, tenant?.emergencyContact?.phone, 
                         tenant?.emergencyContact?.relationship];
          totalTenantFields += fields.length;
          completedTenantFields += fields.filter(f => f && f.toString().trim() !== '').length;
        });
        return totalTenantFields > 0 ? (completedTenantFields / totalTenantFields) * 100 : 0;
      
      case 3: // Lease Terms - only count user-filled fields
        let termsProgress = 0;
        let totalTermsFields = 1; // Only startDate is truly required by user
        
        // Check if user filled the required startDate field
        if (data?.terms?.startDate && data.terms.startDate.trim() !== '') {
          termsProgress += 1;
        }
        
        return (termsProgress / totalTermsFields) * 100;
      
      case 4: // Financial Terms - exclude all defaults
        let financialProgress = 0;
        let totalFinancialFields = 2; // monthlyAmount and security deposit
        
        // Check if user filled monthly rent (exclude default 0)
        if (data?.rent?.monthlyAmount && data.rent.monthlyAmount > 0) {
          financialProgress += 1;
        }
        
        // Check if user filled security deposit (exclude default 0)
        if (data?.deposits?.security && data.deposits.security > 0) {
          financialProgress += 1;
        }
        
        // Note: Excluded from progress calculation:
        // - dueDay (defaults to 1)
        // - lateFee.type (defaults to 'fixed')
        // - lateFee.gracePeriod (defaults to 3)
        // - nsfFee (defaults to 25)
        
        return (financialProgress / totalFinancialFields) * 100;
      
      case 5: // Property Policies - OPTIONAL
        // This step is optional and shows 100% by default
        return 100;
        
      case 6: // Utilities & Services - OPTIONAL
        // This step is optional and shows 100% by default
        return 100;
        
      case 7: // Property Features - OPTIONAL
        // This step is optional and shows 100% by default
        return 100;
        
      case 8: // Disclosures - OPTIONAL
        // This step is optional and shows 100% by default
        return 100;
        
      case 9: // Additional Terms - OPTIONAL
        // This step is optional and shows 100% by default
        return 100;
        
      case 10: // Review Step
        // Review step shows progress based on form completion
        // It should show 100% when all required steps are complete
        const requiredStepsProgress: number[] = [
          calculateStepProgress(0, data), // Landlord Info
          calculateStepProgress(1, data), // Property Info
          calculateStepProgress(2, data), // Tenant Info
          calculateStepProgress(3, data), // Lease Terms
          calculateStepProgress(4, data), // Financial Terms
        ];
        const avgRequiredProgress: number = requiredStepsProgress.reduce((acc: number, curr: number) => acc + curr, 0) / requiredStepsProgress.length;
        return avgRequiredProgress;
        
      default:
        return 0; // All steps start at 0% completion
    }
  };

  const overallProgress = calculateOverallProgress();
  const lacksPrintingAccess = !isDevelopment && !hasPrintingAccess;
  const subscriptionRestricted = !isDevelopment && !hasAccess;
  const primaryButtonDisabled = isSubmitting || isGeneratingPDF || lacksPrintingAccess;
  const primaryButtonLabel = (isSubmitting || isGeneratingPDF)
    ? 'Generating Lease...'
    : isDevelopment
      ? 'Generate Lease (Dev Mode)'
      : hasPrintingAccess
        ? (hasAccess ? 'Generate Lease Agreement' : 'Subscribe to Generate PDF')
        : config.MESSAGES.BUTTON_TEXT;

  const handleSaveDraft = () => {
    // If lease already has a name, save directly without showing modal
    if (currentLeaseName && currentLeaseId) {
      handleSaveWithName(currentLeaseName);
    } else {
      setSaveDialogAction('draft');
      setShowSaveDialog(true);
    }
  };



  const handleSaveWithName = async (name: string) => {
    setIsSavingDraft(true);
    setShowSaveDialog(false);
    
    try {
      if (!user) {
        throw new Error('You must be logged in to save');
      }
      
      const formData = methods.getValues();

      // Sanitize data to avoid phantom defaults leaking into saved leases
      const sanitizedData = (() => {
        const data = JSON.parse(JSON.stringify(formData));

        // Deposits: remove 'other' if not explicitly set
        if (data?.deposits) {
          const normalizedOtherDeposits: Array<{ amount: number; description: string }> = [];

          // Fold legacy single "other" deposit into array shape
          if (data.deposits?.other?.amount && (data.deposits.other.description || '').trim()) {
            normalizedOtherDeposits.push({
              amount: data.deposits.other.amount,
              description: data.deposits.other.description.trim(),
            });
          }

          if (Array.isArray(data.deposits.otherDeposits)) {
            data.deposits.otherDeposits.forEach((item: any) => {
              const amount = Number(item?.amount) || 0;
              const description = (item?.description || '').trim();
              if (amount > 0 && description) {
                normalizedOtherDeposits.push({ amount, description });
              }
            });
          }

          if (normalizedOtherDeposits.length) {
            data.deposits.otherDeposits = normalizedOtherDeposits;
          } else {
            delete data.deposits.otherDeposits;
          }
          if (data.deposits?.other) delete data.deposits.other;

          // Ensure pet deposit lives under policies (remove legacy deposits.pet)
          if (data.deposits?.pet) delete data.deposits.pet;
        }

        // Rent: remove otherMonthlyCharges if not explicitly set
        if (data?.rent) {
          const omcAmt = data.rent?.otherMonthlyCharges?.amount || 0;
          const omcDesc = (data.rent?.otherMonthlyCharges?.description || '').trim();
          if (!omcAmt || !omcDesc) {
            if (data.rent?.otherMonthlyCharges) delete data.rent.otherMonthlyCharges;
          }
        }

        // Pets: if not allowed, zero out pet rent/deposit
        if (data?.policies?.pets && data.policies.pets.allowed === false) {
          data.policies.pets.deposit = 0;
          data.policies.pets.rent = 0;
        }

        // Remove empty tenant notice address
        if (data?.tenantNoticeAddress) {
          const { street, city, state, zip } = data.tenantNoticeAddress;
          const hasNoticeAddress = [street, city, state, zip].some((val) => (val || '').toString().trim() !== '');
          if (!hasNoticeAddress) {
            delete data.tenantNoticeAddress;
          }
        }

        return data;
      })();
      
      if (currentLeaseId) {
        // For existing leases, just update without changing status
        await updateLeaseAgreement(currentLeaseId, sanitizedData, name);
        setCurrentLeaseName(name);
        
        setSnackbar({
          open: true,
          message: 'Lease saved successfully!',
          severity: 'success'
        });
      } else {
        // For new leases, determine status based on completion
        const isComplete = overallProgress === 100;
        
        const savedLeaseId = await saveDraftLease(sanitizedData, user.uid, undefined, name);
        setCurrentLeaseId(savedLeaseId);
        setCurrentLeaseName(name);
        
        // If complete, update status to active
        if (isComplete) {
          await updateLeaseStatus(savedLeaseId, 'active');
        }
        
        setSnackbar({
          open: true,
          message: isComplete ? 'Lease completed and saved!' : 'Draft saved successfully!',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error saving lease:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save lease. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleNext = async () => {
    const fields = getFieldsForStep(activeStep);
    if (fields.length > 0) {
      const isValid = await methods.trigger(fields as any);
      if (!isValid) {
        return; // Don't proceed if validation fails
      }
    }
    
    // On mobile, require 100% completion of current step before proceeding
    if (isMobile) {
      const currentStepProgress = calculateStepProgress(activeStep, formData);
      if (currentStepProgress < 100) {
        setSnackbar({
          open: true,
          message: 'Please complete all required fields in this step before continuing.',
          severity: 'warning'
        });
        return; // Don't proceed if step is not 100% complete on mobile
      }
    }
    
    setCompletedSteps(prev => new Set(Array.from(prev).concat(activeStep)));
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStepClick = (step: number) => {
    setActiveStep(step);
  };

  const onSubmit = async (data: any) => {
    // Check subscription status before allowing PDF generation
    if (subscriptionRestricted) {
      setShowPaywall(true);
      return;
    }

    // If lease already has a name, generate directly without showing modal
    if (currentLeaseName && currentLeaseId) {
      handleGenerateWithName(currentLeaseName);
    } else {
      setSaveDialogAction('generate');
      setShowSaveDialog(true);
    }
  };

  const handleGenerateWithName = async (name: string) => {
    setIsSubmitting(true);
    setIsGeneratingPDF(true);
    setShowSaveDialog(false);
    
    try {
      if (!user) {
        throw new Error('You must be logged in to generate a lease');
      }

      // Check if user has printing access
      if (lacksPrintingAccess) {
        setSnackbar({
          open: true,
          message: config.MESSAGES.PRINTING_RESTRICTED,
          severity: 'info'
        });
        setIsSubmitting(false);
        setIsGeneratingPDF(false);
        return;
      }
      
      // First save the lease with the name
      const formData = methods.getValues();
      const savedLeaseId = await saveDraftLease(formData, user.uid, currentLeaseId, name);
      
      // Then generate the PDF
      const { blob } = await generateLeaseDocument(savedLeaseId, name);
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_lease_agreement.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      // Show success state
      setCompletedSteps(prev => new Set(Array.from(prev).concat(activeStep)));
      setSnackbar({
        open: true,
        message: '✅ Lease agreement generated successfully!',
        severity: 'success'
      });
      
      // Navigate back to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error) {
      console.error('Error generating lease PDF:', error);
      setSnackbar({
        open: true,
        message: `Failed to generate lease agreement. Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
      setIsGeneratingPDF(false);
    }
  };

  const getFieldsForStep = (step: number): string[] => {
    switch (step) {
      case 0:
        return ['landlord.fullName', 'landlord.email', 'landlord.phone', 'landlord.address.street', 
                'landlord.address.city', 'landlord.address.state', 'landlord.address.zip'];
      case 1:
        return ['property.address.street', 'property.address.city', 'property.address.state', 
                'property.address.zip', 'property.type', 'property.yearBuilt', 'property.bedrooms', 'property.bathrooms'];
      case 2:
        return ['tenants.0.fullName', 'tenants.0.email', 'tenants.0.phone', 
                'tenants.0.emergencyContact.name', 'tenants.0.emergencyContact.phone', 'tenants.0.emergencyContact.relationship'];
      case 3:
        return ['terms.type', 'terms.startDate', 'terms.noticeRequired'];
      case 4:
        return ['rent.monthlyAmount', 'rent.dueDay', 'deposits.security'];
      default:
        return [];
    }
  };

  const getStepContent = (step: number) => {
    const stepProgress = calculateStepProgress(step, formData);
    
    switch (step) {
      case 0: return <LandlordInfoStep stepProgress={stepProgress} />;
      case 1: return <PropertyInfoStep stepProgress={stepProgress} />;
      case 2: return <TenantInfoStep stepProgress={stepProgress} />;
      case 3: return <LeaseTermsStep stepProgress={stepProgress} />;
      case 4: return <FinancialTermsStep stepProgress={stepProgress} />;
      case 5: return <PropertyPoliciesStep stepProgress={stepProgress} />;
      case 6: return <UtilitiesStep stepProgress={stepProgress} />;
      case 7: return <PropertyFeaturesStep stepProgress={stepProgress} />;
      case 8: return <DisclosuresStep stepProgress={stepProgress} />;
      case 9: return <AdditionalTermsStep stepProgress={stepProgress} />;
      case 10: return <ReviewStep />;
      default: return 'Unknown step';
    }
  };

  const handleEditLeaseName = () => {
    setTempLeaseName(currentLeaseName);
    setIsEditingLeaseName(true);
  };

  const handleSaveLeaseName = () => {
    if (tempLeaseName.trim()) {
      setCurrentLeaseName(tempLeaseName.trim());
    }
    setIsEditingLeaseName(false);
  };

  const handleCancelEditLeaseName = () => {
    setTempLeaseName(currentLeaseName);
    setIsEditingLeaseName(false);
  };

  const loadDemoData = () => {
    const demoData = generateDemoLeaseData();
    methods.reset(demoData);
    setActiveStep(0);
    setCompletedSteps(new Set());
    setCurrentLeaseName(demoData.name || 'Sample Lease');
    setTempLeaseName(demoData.name || 'Sample Lease');
    setSnackbar({ open: true, message: 'Loaded demo lease data.', severity: 'info' });
    if (formContentRef.current) {
      const top = formContentRef.current.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <>
      <NavBar 
        onSaveDraft={handleSaveDraft}
        showSaveDraft={true}
        isSaving={isSavingDraft}
        progress={overallProgress}
        showProgress={true}
      />
      
      {/* Reusable PDF Generation Loader */}
      <PDFGenerationLoader open={isGeneratingPDF} />
      
      <Box sx={{ 
        minHeight: '100vh',
        background: theme.palette.mode === 'dark' 
          ? 'radial-gradient(ellipse at bottom, #1B1B3A 0%, #090a0f 100%)'
          : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated grid background for dark mode */}
        {theme.palette.mode === 'dark' && (
          <Box sx={{
            position: 'absolute',
            width: '200%',
            height: '200%',
            top: '-50%',
            left: '-50%',
            background: `
              linear-gradient(rgba(99, 102, 241, 0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(99, 102, 241, 0.03) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
            transform: 'translate(0px, 0px)',
            transition: 'transform 0.3s ease-out',
            pointerEvents: 'none'
          }} />
        )}

        {/* Floating orbs for dark mode */}
        {theme.palette.mode === 'dark' && (
          <>
            <Box sx={{
              position: 'absolute',
              width: '400px',
              height: '400px',
              background: 'radial-gradient(circle, rgba(99, 102, 241, 0.2) 0%, transparent 70%)',
              borderRadius: '50%',
              top: '10%',
              left: '-10%',
              filter: 'blur(40px)',
              animation: 'float 8s ease-in-out infinite',
              pointerEvents: 'none'
            }} />
            <Box sx={{
              position: 'absolute',
              width: '300px',
              height: '300px',
              background: 'radial-gradient(circle, rgba(168, 85, 247, 0.2) 0%, transparent 70%)',
              borderRadius: '50%',
              bottom: '10%',
              right: '-5%',
              filter: 'blur(40px)',
              animation: 'float 8s ease-in-out infinite reverse',
              pointerEvents: 'none'
            }} />
          </>
        )}

        {/* Header Section - Full Width */}
        <Box sx={{ 
          background: 'transparent',
          color: theme.palette.mode === 'dark' ? 'white' : '#1F2937',
          py: { xs: 3, md: 6 },
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          display: { xs: 'none', md: 'block' }
        }}>
          <Container maxWidth="lg">
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              mb: 3
            }}>
              <Typography variant="h3" sx={{ 
                fontWeight: 900, 
                mb: 1,
                display: { xs: 'none', md: 'block' },
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: 1.2,
                background: 'linear-gradient(90deg, #6366F1 0%, #A855F7 50%, #EC4899 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                animation: 'gradient 3s ease infinite',
                backgroundSize: '200% auto'
              }}>
                California Lease Agreement Generator
              </Typography>
            </Box>
            {currentLeaseId ? (
              <Box sx={{ 
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                mb: 3
              }}>
                {isEditingLeaseName ? (
                  <TextField
                    size="small"
                    value={tempLeaseName}
                    onChange={(e) => setTempLeaseName(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSaveLeaseName();
                      } else if (e.key === 'Escape') {
                        handleCancelEditLeaseName();
                      }
                    }}
                    autoFocus
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={handleSaveLeaseName}
                            sx={{ color: theme.palette.mode === 'dark' ? 'white' : '#1F2937' }}
                          >
                            <CheckIcon2 />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={handleCancelEditLeaseName}
                            sx={{ color: theme.palette.mode === 'dark' ? 'white' : '#1F2937' }}
                          >
                            <CloseIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                      sx: {
                        color: theme.palette.mode === 'dark' ? 'white' : '#1F2937',
                        '& .MuiOutlinedInput-root': {
                          color: theme.palette.mode === 'dark' ? 'white' : '#1F2937',
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.8)',
                          '& fieldset': {
                            borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(31,41,55,0.3)',
                          },
                          '&:hover fieldset': {
                            borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(31,41,55,0.5)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: theme.palette.mode === 'dark' ? 'white' : '#1F2937',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(31,41,55,0.7)',
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: theme.palette.mode === 'dark' ? 'white' : '#1F2937',
                        },
                      }
                    }}
                  />
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    cursor: 'pointer',
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.3)' : '1px solid rgba(31,41,55,0.3)',
                    background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(31,41,55,0.5)',
                      background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.9)'
                    }
                  }}
                  onClick={handleEditLeaseName}
                >
                  <DescriptionIcon sx={{ mr: 1, color: theme.palette.mode === 'dark' ? 'white' : '#1F2937' }} />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: theme.palette.mode === 'dark' ? 'white' : '#1F2937', 
                      fontWeight: 600,
                      mr: 1
                    }}
                  >
                    {currentLeaseName}
                  </Typography>
                  <EditIcon sx={{ fontSize: 16, color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(31,41,55,0.7)' }} />
                </Box>
                )}
              </Box>
            ) : (
              <Typography variant="body1" sx={{ 
                opacity: 0.8, 
                mb: 3,
                display: { xs: 'none', md: 'block' },
                color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(31, 41, 55, 0.8)'
              }}>
                Create a California-focused lease draft with guided steps (not legal advice)
              </Typography>
            )}
            
            {/* Overall Progress - Only visible on mobile */}
            {!(isMobile && hideMobileProgress) && (
            <Box sx={{ 
              mb: 2,
              display: { xs: 'block', md: 'none' }
            }}>
              {/* Dark Mode Toggle for Mobile */}
              <Box sx={{ 
                display: { xs: 'flex', md: 'none' },
                justifyContent: 'flex-end', 
                mb: 2
              }}>
                <DarkModeToggle 
                  size="medium" 
                  color="inherit"
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Overall Progress
                </Typography>
                <Chip 
                  icon={overallProgress === 100 ? <CelebrationIcon /> : <CheckIcon />}
                  label={`${Math.round(overallProgress)}%`}
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    color: 'white',
                    fontWeight: 600
                  }}
                />
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={overallProgress} 
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    background: 'linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%)'
                  }
                }} 
              />
              
              {/* Current Step Progress - Mobile Only */}
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Current Step: {steps[activeStep]}
                  </Typography>
                  <Chip 
                    icon={calculateStepProgress(activeStep, formData) === 100 ? <CheckIcon /> : undefined}
                    label={`${Math.round(calculateStepProgress(activeStep, formData))}%`}
                    size="small"
                    sx={{ 
                      bgcolor: calculateStepProgress(activeStep, formData) === 100 
                        ? 'rgba(16, 185, 129, 0.2)' 
                        : 'rgba(255,255,255,0.2)', 
                      color: 'white',
                      fontWeight: 600
                    }}
                  />
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={calculateStepProgress(activeStep, formData)} 
                  sx={{ 
                    height: 6, 
                    borderRadius: 3,
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 3,
                      background: calculateStepProgress(activeStep, formData) === 100 
                        ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)'
                        : 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)'
                    }
                  }} 
                />
              </Box>
            </Box>
            )}
            
            {/* Show dark mode toggle even when progress is hidden */}
            {isMobile && hideMobileProgress && (
              <Box sx={{ 
                display: { xs: 'flex', md: 'none' },
                justifyContent: 'flex-end', 
                mb: 2
              }}>
                <DarkModeToggle 
                  size="medium" 
                  color="inherit"
                />
              </Box>
            )}

            <Typography variant="body1" sx={{ 
              opacity: 0.8,
              display: { xs: 'none', md: 'block' },
              fontSize: '1.1rem',
              fontWeight: 500,
              color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(31, 41, 55, 0.8)'
            }}>
              💡 <strong>Tip:</strong> Click on any step below to navigate directly to that section
            </Typography>
          </Container>
        </Box>

        {isDevelopment && (
          <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 3 }, mb: { xs: 2, md: 3 } }}>
            <Alert
              severity="info"
              sx={{ borderRadius: 2 }}
              action={
                <Button color="inherit" variant="outlined" size="small" onClick={loadDemoData}>
                  Load Demo Lease
                </Button>
              }
            >
              Dev helper: instantly populate realistic sample data and generate PDFs without subscription checks.
            </Alert>
          </Container>
        )}

        {/* Form Section */}
        {isMobile ? (
          // Mobile: Full screen layout without container constraints
          <Box ref={formContentRef} sx={{ 
            minHeight: '100vh',
            width: '100%',
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(145deg, #111827 0%, #1f2937 100%)'
              : 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
            borderRadius: 0,
            overflow: 'hidden',
            pb: 4
          }}>
            <Box sx={{ p: { xs: 2, md: 4 } }}>
              <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)}>
                  {/* Current Step Content */}
                  <Box sx={{ mb: 3 }}>
                    {getStepContent(activeStep)}
                  </Box>
                  

                  
                  {/* Navigation Buttons */}
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 2,
                    mt: 3,
                    pt: 3,
                    borderTop: theme.palette.mode === 'dark'
                      ? '1px solid rgba(99, 102, 241, 0.2)'
                      : '1px solid rgba(99, 102, 241, 0.1)',
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(31, 41, 55, 0.9)'
                      : 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(5px)',
                    borderRadius: 2,
                    p: 2
                  }}>
                    {activeStep === steps.length - 1 ? (
                      <Zoom in={true}>
                        <Button
                          variant="contained"
                          size="large"
                          type="submit"
                          disabled={primaryButtonDisabled}
                          startIcon={isSubmitting || isGeneratingPDF ? undefined : <DownloadIcon />}
                          fullWidth
                          sx={{ 
                            borderRadius: 2,
                            background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: 6
                            }
                          }}
                        >
                          {primaryButtonLabel}
                        </Button>
                      </Zoom>
                    ) : (
                      <Button
                        variant="contained"
                        onClick={handleNext}
                        endIcon={<ArrowForwardIcon />}
                        size="large"
                        fullWidth
                        disabled={isMobile && calculateStepProgress(activeStep, formData) < 100}
                        sx={{ 
                          borderRadius: 2,
                          opacity: isMobile && calculateStepProgress(activeStep, formData) < 100 ? 0.6 : 1
                        }}
                      >
                        {isMobile && calculateStepProgress(activeStep, formData) < 100 
                          ? `Complete Step (${Math.round(calculateStepProgress(activeStep, formData))}%)` 
                          : 'Continue'
                        }
                      </Button>
                    )}
                    
                    <Button
                      disabled={activeStep === 0}
                      onClick={handleBack}
                      startIcon={<ArrowBackIcon />}
                      variant="outlined"
                      size="large"
                      fullWidth
                      sx={{ borderRadius: 2 }}
                    >
                      Back
                    </Button>
                  </Box>
                </form>
              </FormProvider>
            </Box>
          </Box>
        ) : (
          // Desktop: Container with card layout
          <Container maxWidth="lg" sx={{ mt: 4, pb: 4 }}>
            <Paper ref={formContentRef} elevation={8} sx={{ 
              borderRadius: 4, 
              overflow: 'hidden',
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(145deg, #1f2937 0%, #374151 100%)'
                : 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
              border: theme.palette.mode === 'dark' 
                ? '1px solid rgba(255,255,255,0.1)'
                : '1px solid rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)'
            }}>
              <Box sx={{ p: { xs: 2, md: 4 } }}>
                <FormProvider {...methods}>
                  <form onSubmit={methods.handleSubmit(onSubmit)}>
                    {/* Desktop: Sidebar + Content Layout */}
                    <Grid container spacing={4} sx={{ minHeight: '70vh' }}>
                      {/* Left Sidebar - Progress Steps */}
                      <Grid size={{ xs: 12, md: 4, lg: 3 }}>
                        <Paper 
                          elevation={2} 
                          sx={{ 
                            background: theme.palette.mode === 'dark'
                              ? 'linear-gradient(145deg, rgba(31, 41, 55, 0.8) 0%, rgba(17, 24, 39, 0.8) 100%)'
                              : 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                            border: theme.palette.mode === 'dark'
                              ? '1px solid rgba(99, 102, 241, 0.2)'
                              : '1px solid rgba(99, 102, 241, 0.1)', 
                            borderRadius: 3,
                            position: 'sticky',
                            top: 20,
                            height: 'fit-content',
                            boxShadow: theme.palette.mode === 'dark'
                              ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                              : '0 8px 32px rgba(99, 102, 241, 0.1)',
                            overflow: 'hidden'
                          }}
                        >
                          <Box sx={{ 
                            p: 3,
                            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                            color: 'white'
                          }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                              ✨ Progress
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={overallProgress} 
                                sx={{ 
                                  height: 8, 
                                  borderRadius: 4,
                                  backgroundColor: 'rgba(255,255,255,0.2)',
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 4,
                                    background: 'linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%)'
                                  }
                                }} 
                              />
                              <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                                {Math.round(overallProgress)}% Complete
                              </Typography>
                            </Box>
                          </Box>
                          
                          <List disablePadding>
                            {shortSteps.map((label, index) => {
                              const stepProgress = calculateStepProgress(index, formData);
                              const isCompleted = stepProgress === 100;
                              const isActive = index === activeStep;
                              
                              return (
                                <ListItem key={label} disablePadding>
                                  <ListItemButton
                                    onClick={() => handleStepClick(index)}
                                    selected={isActive}
                                    sx={{
                                      py: 2,
                                      px: 3,
                                      mx: 1,
                                      my: 0.5,
                                      borderRadius: 2,
                                      transition: 'all 0.3s ease-in-out',
                                      backgroundColor: isActive 
                                        ? theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.1)' 
                                        : [5, 6, 7, 8, 9].includes(index) 
                                          ? theme.palette.mode === 'dark' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.05)' // Light orange background for optional
                                          : 'transparent',
                                      border: isActive 
                                        ? theme.palette.mode === 'dark' ? '1px solid rgba(99, 102, 241, 0.4)' : '1px solid rgba(99, 102, 241, 0.3)' 
                                        : [5, 6, 7, 8, 9].includes(index) 
                                          ? theme.palette.mode === 'dark' ? '1px dashed rgba(245, 158, 11, 0.4)' : '1px dashed rgba(245, 158, 11, 0.3)' // Dashed border for optional
                                          : '1px solid transparent',
                                      '&:hover': {
                                        backgroundColor: isActive 
                                          ? theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.25)' : 'rgba(99, 102, 241, 0.15)' 
                                          : [5, 6, 7, 8, 9].includes(index) 
                                            ? theme.palette.mode === 'dark' ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.1)' // Orange hover for optional
                                            : theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.1)' : 'rgba(99, 102, 241, 0.05)',
                                        transform: 'translateX(4px)',
                                        boxShadow: [5, 6, 7, 8, 9].includes(index) 
                                          ? theme.palette.mode === 'dark' ? '0 4px 12px rgba(245, 158, 11, 0.25)' : '0 4px 12px rgba(245, 158, 11, 0.15)' // Orange shadow for optional
                                          : theme.palette.mode === 'dark' ? '0 4px 12px rgba(99, 102, 241, 0.25)' : '0 4px 12px rgba(99, 102, 241, 0.15)'
                                      },
                                      '&.Mui-selected': {
                                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.1)',
                                        '&:hover': {
                                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.25)' : 'rgba(99, 102, 241, 0.15)'
                                        }
                                      }
                                    }}
                                  >
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                      <Box
                                        sx={{
                                          width: 24,
                                          height: 24,
                                          borderRadius: '50%',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontSize: '0.75rem',
                                          fontWeight: 600,
                                          background: isCompleted && ![5, 6, 7, 8, 9].includes(index)
                                            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                                            : isActive 
                                              ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' 
                                              : [5, 6, 7, 8, 9].includes(index) 
                                                ? theme.palette.mode === 'dark' ? '#92400e' : '#fef3c7' // Dark/light yellow for optional steps
                                                : theme.palette.mode === 'dark' ? '#374151' : '#e5e7eb',
                                          color: (isCompleted && ![5, 6, 7, 8, 9].includes(index)) || isActive ? 'white' : [5, 6, 7, 8, 9].includes(index) ? '#d97706' : theme.palette.mode === 'dark' ? '#9ca3af' : '#6b7280',
                                          boxShadow: (isCompleted && ![5, 6, 7, 8, 9].includes(index)) || isActive ? (theme.palette.mode === 'dark' ? '0 2px 8px rgba(99, 102, 241, 0.4)' : '0 2px 8px rgba(99, 102, 241, 0.3)') : 'none',
                                          border: [5, 6, 7, 8, 9].includes(index) 
                                            ? isActive 
                                              ? theme.palette.mode === 'dark' ? '2px solid rgba(99, 102, 241, 0.4)' : '2px solid rgba(99, 102, 241, 0.3)' // Purple solid border when active
                                              : '2px dashed #f59e0b' // Orange dashed border when not active
                                            : 'none'
                                        }}
                                      >
                                        {(isCompleted && ![5, 6, 7, 8, 9].includes(index)) ? '✓' : [5, 6, 7, 8, 9].includes(index) ? '?' : index === 10 ? <PrintIcon sx={{ fontSize: '1rem' }} /> : index + 1}
                                      </Box>
                                    </ListItemIcon>
                                    <ListItemText 
                                      primary={
                                        <Box>
                                          <Typography 
                                            variant="body2" 
                                            sx={{ 
                                              fontWeight: isActive ? 600 : 400,
                                              color: isActive ? 'primary.main' : 'text.primary'
                                            }}
                                          >
                                            {label}
                                            {[5, 6, 7, 8, 9].includes(index) && (
                                              <Typography 
                                                component="span" 
                                                variant="caption" 
                                                sx={{ 
                                                  fontSize: '0.65rem',
                                                  color: theme.palette.mode === 'dark' ? '#9ca3af' : '#6b7280',
                                                  fontStyle: 'italic',
                                                  ml: 0.5
                                                }}
                                              >
                                                optional
                                              </Typography>
                                            )}
                                          </Typography>
                                          {![5, 6, 7, 8, 9, 10].includes(index) && (
                                            <LinearProgress 
                                              variant="determinate" 
                                              value={stepProgress} 
                                              sx={{ 
                                                height: 6, 
                                                borderRadius: 3,
                                                mt: 0.5,
                                                backgroundColor: theme.palette.mode === 'dark' ? '#374151' : '#f3f4f6',
                                                '& .MuiLinearProgress-bar': {
                                                  borderRadius: 3,
                                                  background: isCompleted 
                                                    ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)'
                                                    : 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)'
                                                }
                                              }} 
                                            />
                                          )}
                                          {![5, 6, 7, 8, 9, 10].includes(index) && (
                                            <Typography 
                                              variant="caption" 
                                              sx={{ 
                                                fontSize: '0.7rem',
                                                fontWeight: 500,
                                                color: isActive ? '#6366f1' : (theme.palette.mode === 'dark' ? '#9ca3af' : '#6b7280')
                                              }}
                                            >
                                              {Math.round(stepProgress)}%
                                            </Typography>
                                          )}
                                        </Box>
                                      }
                                    />
                                  </ListItemButton>
                                </ListItem>
                              );
                            })}
                          </List>
                        </Paper>
                      </Grid>
                      
                      {/* Right Content Area */}
                      <Grid size={{ xs: 12, md: 8, lg: 9 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                          <Fade in={true} timeout={500} key={activeStep}>
                            <Card elevation={1} square={false}>
                              <CardContent sx={{ 
                                p: { xs: 3, md: 4, lg: 5 }, 
                                minHeight: 500,
                                background: theme.palette.mode === 'dark'
                                  ? 'rgba(31, 41, 55, 0.7)'
                                  : 'rgba(255, 255, 255, 0.7)',
                                backdropFilter: 'blur(10px)',
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column'
                              }}>
                                <Box sx={{ flex: 1 }}>
                                  {getStepContent(activeStep)}
                                </Box>
                                
                                {/* Navigation Buttons - Sticky at bottom of content */}
                                <Box sx={{ 
                                  display: 'flex', 
                                  flexDirection: { xs: 'column', sm: 'row' },
                                  gap: 2,
                                  pt: 3,
                                  mt: 3,
                                  borderTop: theme.palette.mode === 'dark'
                                    ? '1px solid rgba(99, 102, 241, 0.2)'
                                    : '1px solid rgba(99, 102, 241, 0.1)',
                                  backgroundColor: theme.palette.mode === 'dark'
                                    ? 'rgba(31, 41, 55, 0.9)'
                                    : 'rgba(255, 255, 255, 0.9)',
                                  backdropFilter: 'blur(5px)',
                                  borderRadius: 2,
                                  position: 'sticky',
                                  bottom: 0,
                                  zIndex: 10
                                }}>
                                  <Button
                                    disabled={activeStep === 0}
                                    onClick={handleBack}
                                    startIcon={<ArrowBackIcon />}
                                    variant="outlined"
                                    size="large"
                                    sx={{ 
                                      borderRadius: 2,
                                      minWidth: { xs: '100%', sm: 140 }
                                    }}
                                  >
                                    Back
                                  </Button>
                                  
                                  <Box sx={{ flex: 1 }} />
                                  
                                  {activeStep === steps.length - 1 ? (
                                    <Zoom in={true}>
                                      <Button
                                        variant="contained"
                                        size="large"
                                        type="submit"
                                        disabled={primaryButtonDisabled}
                                        startIcon={isSubmitting || isGeneratingPDF ? undefined : <DownloadIcon />}
                                        sx={{ 
                                          borderRadius: 2,
                                          minWidth: { xs: '100%', sm: 200 },
                                          background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                                          '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: 6
                                          }
                                        }}
                                      >
                                        {primaryButtonLabel}
                                      </Button>
                                    </Zoom>
                                  ) : (
                                    <Button
                                      variant="contained"
                                      onClick={handleNext}
                                      endIcon={<ArrowForwardIcon />}
                                      size="large"
                                      sx={{ 
                                        borderRadius: 2,
                                        minWidth: { xs: '100%', sm: 140 }
                                      }}
                                    >
                                      Next
                                    </Button>
                                  )}
                                </Box>
                              </CardContent>
                            </Card>
                          </Fade>
                        </Box>
                      </Grid>
                    </Grid>
                  </form>
                </FormProvider>
              </Box>
            </Paper>
          </Container>
        )}
      </Box>
      
      <SaveLeaseDialog
        open={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        onSave={saveDialogAction === 'draft' ? handleSaveWithName : handleGenerateWithName}
        title={saveDialogAction === 'draft' ? "Save" : "Name Your Lease"}
        defaultName={currentLeaseName}
        loading={saveDialogAction === 'draft' ? isSavingDraft : isSubmitting}
      />
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Paywall Dialog */}
      <PaywallDialog
        open={showPaywall}
        onClose={() => setShowPaywall(false)}
      />
    </>
  );
};

export default LeaseForm; 
